import React, { useEffect, useState } from "react";
import {
  CHART_PREF_EVENT,
  COLOR_TEMPLATES,
  getColorTemplate,
  loadChartPreferences,
  saveChartPreferences,
} from "utils/chartPreferences";
import useAppLanguage from "hooks/useAppLanguage";

export function colorTemplateLabel(tpl, t) {
  if (!tpl) return "";
  return tpl.labelKey ? t(tpl.labelKey) : tpl.shortLabel || tpl.label;
}

export function TemplateCandleIcon({ upColor, downColor, compact = false }) {
  const w = compact ? 28 : 34;
  const h = compact ? 24 : 30;
  return (
    <svg
      className="fx-color-templates-icon"
      width={w}
      height={h}
      viewBox="0 0 34 30"
      aria-hidden
    >
      <line x1="10" y1="5" x2="10" y2="25" stroke={upColor} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="7" y="9" width="6" height="10" fill={upColor} rx="0.5" />
      <line x1="24" y1="7" x2="24" y2="27" stroke={downColor} strokeWidth="1.5" strokeLinecap="round" />
      <rect x="21" y="13" width="6" height="10" fill={downColor} rx="0.5" />
    </svg>
  );
}

export function ColorTemplateCheckMark() {
  return (
    <svg className="fx-color-templates-check" width="18" height="18" viewBox="0 0 22 22" aria-hidden>
      <path
        d="M5 11.2l3.4 3.4L17 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Apply an existing color template (same prefs path as the full page). */
export function applyColorTemplateId(id, onApplied) {
  saveChartPreferences({ colorTemplateId: id });
  onApplied?.(getColorTemplate(id).label);
  return id;
}

export default function ColorTemplatesPage({ onApplied }) {
  const { t } = useAppLanguage();
  const [selectedId, setSelectedId] = useState(() => loadChartPreferences().colorTemplateId);

  useEffect(() => {
    const onPrefs = (e) => {
      const id = e.detail?.colorTemplateId ?? loadChartPreferences().colorTemplateId;
      setSelectedId(id);
    };
    window.addEventListener(CHART_PREF_EVENT, onPrefs);
    return () => window.removeEventListener(CHART_PREF_EVENT, onPrefs);
  }, []);

  const apply = (id) => {
    setSelectedId(id);
    applyColorTemplateId(id, () => onApplied?.(colorTemplateLabel(getColorTemplate(id), t)));
  };

  return (
    <div className="fx-color-templates-page">
      <ul className="fx-color-templates-list" role="listbox" aria-label={t("colorTemplates")}>
        {COLOR_TEMPLATES.map((tpl) => {
          const active = selectedId === tpl.id;
          const label = colorTemplateLabel(tpl, t);
          return (
            <li key={tpl.id}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                className={`fx-color-templates-row${active ? " fx-color-templates-row--active" : ""}`}
                onClick={() => apply(tpl.id)}
              >
                <span className="fx-color-templates-row__icon">
                  <TemplateCandleIcon upColor={tpl.upColor} downColor={tpl.downColor} />
                </span>
                <span className="fx-color-templates-row__label">{label}</span>
                {active ? (
                  <span className="fx-color-templates-row__check" aria-label={t("selected")}>
                    <ColorTemplateCheckMark />
                  </span>
                ) : (
                  <span className="fx-color-templates-row__check fx-color-templates-row__check--empty" />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
